import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

import Empty from '../empty/Empty';
import InfiniteScroll from '../infiniteScroll/InfiniteScroll';
import SchemaOrgItemList from '../schemaOrg/SchemaOrgItemList';

import './CardDeck.css';

/**
 * CardDeck 의 presentational(표현) 뷰.
 *
 * 데이터 fetch/pagination(어떤 쿼리를 쓰는지, 응답이 flat 인지 nested 인지)은 상위 컨테이너가 소유하고,
 * 이 컴포넌트는 "이미 받아온 items 배열"만 받아 리스팅 UI 를 렌더한다:
 *   - 반응형 카드 그리드 (Bootstrap row/col 클래스)
 *   - 무한 스크롤(InfiniteScroll)
 *   - schema.org ItemList(JSON-LD)
 *   - 결과 0건 시 안내(Empty) + SEO 처리(noindex / 404)
 *
 * 응답 모양과 무관하게 재사용 가능하므로, flat 쿼리(searchProducts)든
 * nested 쿼리(getCatalogModelWithProducts.products.items)든 동일한 리스팅을 얻는다.
 */
export default function CardDeckView({
    children,
    items,
    loading = false,
    error,
    rowClassName,
    hasMore,
    loadMore,
    offset = 0,
    isEmptyMsg,
    schemaOrgPreview,
    keyword,
    noindexWhenEmpty = false,
    notFoundWhenEmpty = false,
}) {
    // 결과가 "확실히 0건"인지 판정한다.
    const isConfirmedEmpty = !loading && !error && Array.isArray(items) && items.length === 0;

    const renderItem = (item, index) => (
        <li className="CardDeck-item col mb-4" key={item.id}>
            {children(item, index)}
        </li>
    );
    const listItems = items && items.length
        ? items.map(renderItem)
        : loading
            ? null
            : (
                <li className="CardDeck-empty">
                    {isEmptyMsg && <Empty />}
                </li>
            );

    return (
        <>
            {/* 검색 결과 0건 페이지가 색인되면 기생 SEO 스팸의 표적이 되므로 크롤러에서 제외한다 */}
            {noindexWhenEmpty && isConfirmedEmpty && (
                <Helmet>
                    <meta name="robots" content="noindex" />
                </Helmet>
            )}
            {/* 이미 색인된 스팸 URL의 제거를 앞당기기 위해 상태코드도 404로 내린다.
                (server/extractStatus.js 가 이 메타를 읽어 HTTP status 로 변환) */}
            {noindexWhenEmpty && notFoundWhenEmpty && isConfirmedEmpty && (
                <Helmet>
                    <meta httpEquiv="Status" content="404 Not Found" />
                </Helmet>
            )}
            <div className="CardDeck">
                <InfiniteScroll loading={loading} hasMore={hasMore} loadMore={loadMore}>
                    <ul className={`CardDeck-list list-unstyled row ${rowClassName}`}>
                        {listItems}
                    </ul>
                </InfiniteScroll>
            </div>
            {/* SSR 최초 렌더링 시에만 JSON-LD 출력 */}
            <SchemaOrgItemList
                offset={offset}
                items={items || []}
                schemaOrgPreview={schemaOrgPreview}
                keyword={keyword}
            />
        </>
    );
}

CardDeckView.propTypes = {
    children: PropTypes.func.isRequired,
    items: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.object,
    rowClassName: PropTypes.string.isRequired,
    hasMore: PropTypes.bool,
    loadMore: PropTypes.func,
    offset: PropTypes.number,
    isEmptyMsg: PropTypes.bool,
    schemaOrgPreview: PropTypes.object,
    keyword: PropTypes.string,
    noindexWhenEmpty: PropTypes.bool,
    notFoundWhenEmpty: PropTypes.bool,
};
